import { PipelineSeriesByRegionColour, PipelineSeriesOnBuild } from '@nova/types';

import { IScaleParams } from '../models/scale-params.interface';
import { IScalePipelineResult } from '../models/scale-pipeline-result.interface';

import {
  applyColourMatrixPixels,
  applyMaskPixels,
  applyOverlayPixels,
  applySummationPixels,
  applyTintPixels,
  runPipelineSeriesOnImageData,
  runRegionPipelineSeriesOnImageData
} from './pixel-processors';
import { cloneImageData, createBlankImageData, imageDataToGlPixels } from './pixel-utils';
import { IRenderPipeline } from './render-pipeline.interface';

export class Canvas2DRenderPipeline implements IRenderPipeline {
  public readonly type = 'canvas2d' as const;

  private _canvas?: HTMLCanvasElement;
  private _ctx?: CanvasRenderingContext2D;

  public constructor(initialWidth = 1, initialHeight = 1) {
    this.ensureCanvas(initialWidth, initialHeight);
  }

  public init(width: number, height: number): void {
    this.ensureCanvas(width, height);
  }

  public resize(width: number, height: number): void {
    this.ensureCanvas(width, height);
  }

  public destroy(): void {
    this._canvas?.remove();
    this._canvas = undefined;
    this._ctx = undefined;
  }

  public copy(image: ImageData): Uint8Array {
    return imageDataToGlPixels(cloneImageData(image));
  }

  public tile(image: ImageData, finalWidth: number, finalHeight: number): Uint8Array {
    this.ensureCanvas(finalWidth, finalHeight);
    if (!this._ctx || !this._canvas) {
      throw new Error('Canvas2DRenderPipeline::tile: Canvas context unavailable.');
    }

    this._ctx.clearRect(0, 0, finalWidth, finalHeight);
    const tileCanvas = document.createElement('canvas');
    tileCanvas.width = image.width;
    tileCanvas.height = image.height;
    const tileCtx = tileCanvas.getContext('2d');
    if (!tileCtx) {
      throw new Error('Canvas2DRenderPipeline::tile: Could not create tile canvas context.');
    }

    tileCtx.putImageData(image, 0, 0);
    const pattern = this._ctx.createPattern(tileCanvas, 'repeat');
    if (!pattern) {
      throw new Error('Canvas2DRenderPipeline::tile: Could not create tile pattern.');
    }

    this._ctx.fillStyle = pattern;
    this._ctx.fillRect(0, 0, finalWidth, finalHeight);
    return imageDataToGlPixels(this._ctx.getImageData(0, 0, finalWidth, finalHeight));
  }

  public mask(imageToMask: ImageData, imageAsMask: ImageData, invertMaskEffect: boolean): Uint8Array {
    return imageDataToGlPixels(applyMaskPixels(imageToMask, imageAsMask, invertMaskEffect));
  }

  public overlay(imageBottom: ImageData, imageTop: ImageData): Uint8Array {
    return imageDataToGlPixels(applyOverlayPixels(imageBottom, imageTop));
  }

  public tint(image: ImageData, colour: number[]): Uint8Array {
    return imageDataToGlPixels(applyTintPixels(image, colour));
  }

  public scale(image: ImageData, scaleParams: IScaleParams): IScalePipelineResult {
    let scaleX = scaleParams.widthPct || 1;
    let scaleY = scaleParams.heightPct || 1;

    if (scaleParams.widthPct === undefined && scaleParams.widthToPx !== undefined) {
      scaleX = scaleParams.widthToPx / image.width;
    }

    if (scaleParams.heightPct === undefined && scaleParams.heightToPx !== undefined) {
      scaleY = scaleParams.heightToPx / image.height;
    }

    const newWidthPx = Math.max(1, Math.round(image.width * scaleX));
    const newHeightPx = Math.max(1, Math.round(image.height * scaleY));

    this.ensureCanvas(newWidthPx, newHeightPx);
    if (!this._ctx || !this._canvas) {
      throw new Error('Canvas2DRenderPipeline::scale: Canvas context unavailable.');
    }

    this._ctx.clearRect(0, 0, newWidthPx, newHeightPx);
    const sourceCanvas = document.createElement('canvas');
    sourceCanvas.width = image.width;
    sourceCanvas.height = image.height;
    const sourceCtx = sourceCanvas.getContext('2d');
    if (!sourceCtx) {
      throw new Error('Canvas2DRenderPipeline::scale: Could not create source canvas context.');
    }

    sourceCtx.putImageData(image, 0, 0);
    this._ctx.drawImage(sourceCanvas, 0, 0, image.width, image.height, 0, 0, newWidthPx, newHeightPx);
    const pixelData = imageDataToGlPixels(this._ctx.getImageData(0, 0, newWidthPx, newHeightPx));

    return { pixelData, newWidthPx, newHeightPx };
  }

  public colourMatrix(image: ImageData, matrix: Float32Array | number[], alpha: number): Uint8Array {
    return imageDataToGlPixels(applyColourMatrixPixels(image, matrix, alpha));
  }

  public summation(imageA: ImageData, imageB: ImageData): Uint8Array {
    return imageDataToGlPixels(applySummationPixels(imageA, imageB));
  }

  public runPipelineSeries(base: ImageData, seriesBuilt: PipelineSeriesOnBuild): Uint8Array {
    if (seriesBuilt.length === 0) {
      return this.copy(base);
    }

    return imageDataToGlPixels(runPipelineSeriesOnImageData(base, seriesBuilt));
  }

  public runPipelineSeriesByRegionColour(
    base: ImageData,
    mask: ImageData,
    seriesByRegionColour: PipelineSeriesByRegionColour
  ): Uint8Array {
    if (Object.keys(seriesByRegionColour).length === 0) {
      return this.copy(base);
    }

    let accumulated: ImageData | undefined;
    const blankOutput = createBlankImageData(base.width, base.height);

    for (const colourName in seriesByRegionColour) {
      if (!Object.prototype.hasOwnProperty.call(seriesByRegionColour, colourName)) { continue; }

      const seriesBuilt = seriesByRegionColour[colourName];
      const regionResult = runRegionPipelineSeriesOnImageData(base, mask, colourName, seriesBuilt, true);

      if (accumulated) {
        accumulated = applySummationPixels(accumulated, regionResult);
      }
      else {
        accumulated = regionResult;
      }
    }

    const overlayResult = applyOverlayPixels(base, accumulated || blankOutput);
    return imageDataToGlPixels(overlayResult);
  }

  private ensureCanvas(width: number, height: number): void {
    const safeWidth = Math.max(1, width);
    const safeHeight = Math.max(1, height);

    if (this._canvas && this._ctx) {
      this._canvas.width = safeWidth;
      this._canvas.height = safeHeight;
      return;
    }

    const canvas = document.createElement('canvas');
    canvas.width = safeWidth;
    canvas.height = safeHeight;

    // Setting willReadFrequently to true due to frequent use of getImageData which is a CPU intensive operation.
    // Otherwise, the browser will often default to storing the canvas context on the GPU.
    // Note that this is a trade-off that may slow down other operations such as draw/fill.
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    if (!ctx) {
      throw new Error('Canvas2DRenderPipeline: Could not create canvas 2d context.');
    }

    this._canvas = canvas;
    this._ctx = ctx;
  }
}
